import React, { useState, useEffect } from 'react';
import CustomDeviceModal from './CustomDeviceModal';
import { log, error } from '../utils/logger';
import ESP32CustomDownloadCode from './ESP32CustomDownloadCode';


const CustomDevicesContent = ({ userId, isDarkMode, currentPlanLimits }) => {
  const [customDevices, setCustomDevices] = useState([]);
  const [controlsByDevice, setControlsByDevice] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editDevice, setEditDevice] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [deviceToDownload, setDeviceToDownload] = useState(null);


  const API_BASE_URL = 'https://holmio.net/app/api.php';
  const bgColorClass = isDarkMode ? 'bg-gray-800' : 'bg-white';
  const textColorClass = isDarkMode ? 'text-gray-100' : 'text-gray-800';
  const tableHeaderClass = isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700';
  const tableRowClass = isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50';
  const buttonClass = isDarkMode ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-green-500 hover:bg-green-600 text-white';

  useEffect(() => {
    fetchCustomDevices();
  }, []);

  const fetchCustomDevices = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/custom_devices?user_id=${userId}`);
      const data = await response.json();
      if (data.status === 'success') {
        setCustomDevices(data.data);
        fetchControlsForDevices(data.data);
      } else {
        error('Error al obtener dispositivos personalizados:', data.message);
      }
    } catch (err) {
      error('Error de red al obtener dispositivos personalizados:', err);
    }
  };

  const fetchControlsForDevices = async (devices) => {
    try {
      const controlsMap = {};
      for (const device of devices) {
        const res = await fetch(`${API_BASE_URL}/custom_device_controls?device_id=${device.id}`);
        const json = await res.json();
        controlsMap[device.id] = json.status === 'success' ? json.data : [];
      }
      setControlsByDevice(controlsMap);
    } catch (err) {
      console.error('Error cargando controles:', err);
    }
  };
  

  const handleDeleteDevice = async (deviceId) => {
    if (window.confirm('¿Seguro que deseas eliminar este dispositivo?')) {
      try {
        const response = await fetch(`${API_BASE_URL}/custom_devices`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: deviceId }),
        });
        const data = await response.json();
        if (data.status === 'success') {
          setCustomDevices(customDevices.filter((d) => d.id !== deviceId));
        } else {
          alert(`Error al eliminar: ${data.message}`);
        }
      } catch (err) {
        alert(`Error al eliminar dispositivo: ${err.message}`);
      }
    }
  };

  const handleEditDevice = (device) => {
    const controls = controlsByDevice[device.id] || [];
    setEditDevice({ ...device, controls });
    setShowModal(true);
  };

  const handleDownloadCode = (deviceId) => {
    alert(`Generar código para ID ${deviceId} (pendiente integrar generador ESP32)`);
  };

  return (
    <div className={`flex-1 p-6 rounded-lg shadow-xl m-4 ${bgColorClass} ${textColorClass}`}>
      <h2 className="text-3xl font-bold mb-6 text-center">Dispositivos Personalizados</h2>

      {/* Botón para agregar dispositivo */}
      <div className="text-center mb-6">
        <button
          onClick={() => {
            if (customDevices.length >= currentPlanLimits.devices) {
              alert(`Has alcanzado el límite de ${currentPlanLimits.devices} dispositivos para tu plan actual.`);
              return;
            }

            setEditDevice(null);
            setShowModal(true);
          }}
          className={`px-6 py-3 rounded-lg font-semibold transition-colors duration-300 ${buttonClass}`}
        >
          Agregar Dispositivo Personalizado
        </button>
      </div>

      {customDevices.length > 0 ? (
        <div className="overflow-x-auto rounded-lg shadow-md">
          <table className="min-w-full table-auto divide-y divide-gray-200">
            <thead className={tableHeaderClass}>
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase">Nombre</th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase">Board</th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase">Serie</th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase">Controles</th>
                <th className="px-4 py-2 text-center text-xs font-medium uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className={`divide-y divide-gray-200 ${isDarkMode ? 'text-gray-300' : 'text-gray-800'}`}>
              {customDevices.map((device) => (
                <tr key={device.id} className={tableRowClass}>
                  <td className="px-4 py-2 text-sm">{device.name}</td>
                  <td className="px-4 py-2 text-sm">{device.board}</td>
                  <td className="px-4 py-2 text-sm">{device.serial}</td>
                  <td className="px-4 py-2 text-sm max-w-xs overflow-hidden text-ellipsis">
                    {controlsByDevice[device.id] && controlsByDevice[device.id].length > 0
                      ? controlsByDevice[device.id].map((c, idx) => (
                          <span key={idx}>
                            {c.control_name} ({c.description}) {c.unit ? `[${c.unit}]` : ''}
                            {idx < controlsByDevice[device.id].length - 1 ? ', ' : ''}
                          </span>
                        ))
                      : 'Sin controles'}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <div className="flex justify-center gap-3">
                      {/* Editar */}
                      <button onClick={() => handleEditDevice(device)} title="Editar">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500 hover:text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536L15.232 5.232z" />
                        </svg>
                      </button>
                      {/* Eliminar */}
                      <button onClick={() => handleDeleteDevice(device.id)} title="Eliminar">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 hover:text-red-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                      {/* Descargar */}
                      <button
                        onClick={() => {
                          setDeviceToDownload(device);
                          setShowDownloadModal(true);
                        }}
                        title="Descargar Código"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 hover:text-green-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                        </svg>
                      </button>

                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
              {showDownloadModal && deviceToDownload && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                  <div className="flex justify-between items-center border-b p-4">
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                      Generar Código – {deviceToDownload.name}
                    </h2>
                    <button
                      onClick={() => {
                        setShowDownloadModal(false);
                        setDeviceToDownload(null);
                      }}
                      className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-xl"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="p-4">
                    <ESP32CustomDownloadCode
                      deviceSerial={deviceToDownload.serial}
                      selectedDevice={deviceToDownload}
                      isDarkMode={isDarkMode}
                      onClose={() => {
                        setShowDownloadModal(false);
                        setDeviceToDownload(null);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}



        </div>
      ) : (
        <p className="text-center text-lg mt-8">No tienes dispositivos personalizados. ¡Agrega uno para empezar!</p>
      )}

      {showModal && (
        <CustomDeviceModal
          userId={userId}
          isDarkMode={isDarkMode}
          onClose={() => {
            setShowModal(false);
            fetchCustomDevices();
          }}
          editMode={!!editDevice}
          deviceData={editDevice}
        />
      )}
    </div>
  );
};

export default CustomDevicesContent;
